import { Fragment, useEffect } from 'react';
import { Row } from '@tanstack/react-table';
import classNames from 'classnames';

import { useErrorContext } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContext';
import { NavigableError } from 'services/errorsNavigation/errorsNavigation';
import { WEBEDI_CELL_HEIGHT } from 'support/helpers/const/const';

import { AddEmbeddedRowCell } from '../../cells/WebEDI/CreateEdit/AddEmbeddedRowCell';
import { RowWithEmbeddedTableRowInfo } from '../../Types';
import { Td } from '../Td/Td';
import { calculateCellBasicProperties, calculateCellRowSpan, calculateCellStyle } from './helpers';

type TableRowProps<TData> = {
  row: Row<TData>;
  rowIndex: number;
  parentRowIndex?: number;
  totalRows: number;
  showCellBorders: boolean;
  columnsWidth: string;
  rowWithEmbeddedTableRowInfo?: RowWithEmbeddedTableRowInfo<TData>;
};

export function TableRow<TData>({
  row,
  rowIndex,
  parentRowIndex,
  totalRows,
  showCellBorders,
  columnsWidth,
  rowWithEmbeddedTableRowInfo,
}: TableRowProps<TData>) {
  const dataPrefix = row.getAllCells()[0].getContext().table.options.meta?.dataPrefix;
  const prefix = dataPrefix ? `${dataPrefix}.` : '';
  const fieldId = `${prefix}${row.id}`;

  // With this we are making the table attached to the error context, not just the cells
  const { service, useErrorModal, ErrorModalComponent } = useErrorContext<NavigableError>();
  const error = service.useErrorById(fieldId);
  const setCurrentError = service.setCurrentError;
  const numberOfErrors = service.useNumberOfActiveErrors();
  const hasError = Boolean(error);

  const { showErrorModal, setShowErrorModal, floatingStyles, refs } = useErrorModal({
    placement: 'top-start',
    allowedPlacements: ['top-start', 'bottom-start'],
  });

  useEffect(() => {
    if (error && showErrorModal) {
      setCurrentError(error);
    }
  }, [error, setCurrentError, showErrorModal]);

  const isEven = (parentRowIndex ?? rowIndex) % 2 === 0;
  const cellClasses = classNames([
    hasError ? '' : isEven ? 'bg-white' : 'bg-gray-50', // For row-level errors, don't apply background to cells - let the row handle it
    hasError ? 'text-red-700' : 'text-gray-700',
  ]);

  const visibleCells = row.getVisibleCells();

  return (
    <tr
      key={row.id}
      data-field={fieldId}
      tabIndex={error ? 0 : undefined}
      onFocus={() => setShowErrorModal(true)}
      onBlur={() => setShowErrorModal(false)}
      className={classNames({
        'border border-solid !border-red-500': showErrorModal && hasError,
        'bg-red-50 hover:bg-red-100': hasError, // Row-level background and hover effect for errors
      })}
      ref={refs.setReference}
      style={{
        height: `${rowWithEmbeddedTableRowInfo?.rowHeight ?? WEBEDI_CELL_HEIGHT}px`,
      }}
    >
      {visibleCells.map((cell, index) => {
        const cellProperties = calculateCellBasicProperties(cell, index, visibleCells.length);

        const parentId = cell.column.parent?.id;
        const previousParentId = index > 0 ? visibleCells[index - 1].column.parent?.id : undefined;
        const isAddRowForThisEmbeddedTable = parentId
          ? rowWithEmbeddedTableRowInfo?.embeddedTableRowRowSpans[parentId]?.isAddRow
          : false;
        const isSpanningAddRow = isAddRowForThisEmbeddedTable && previousParentId === parentId;

        const rowSpanResult = calculateCellRowSpan(cell, rowWithEmbeddedTableRowInfo);

        if (rowSpanResult.shouldSkipRendering || isSpanningAddRow) {
          return null;
        }

        const cellStyle = calculateCellStyle(cellProperties.isAction, columnsWidth, cellProperties.fixedWidth);

        const isLastRow = (parentRowIndex ?? rowIndex) === totalRows - 1;
        const isLastEmbeddedTableRow = rowWithEmbeddedTableRowInfo
          ? rowWithEmbeddedTableRowInfo?.embeddedTableRowIndex ===
            rowWithEmbeddedTableRowInfo?.embeddedTableRowsCount - 1
          : false;

        if (isAddRowForThisEmbeddedTable) {
          const colSpan = rowWithEmbeddedTableRowInfo?.embeddedTableRowRowSpans[parentId!].colSpan || 1;
          const cellContext = cell.getContext();

          return (
            <Td
              key={cell.id}
              cell={cell}
              isAction
              colSpan={colSpan}
              rowSpan={rowSpanResult.rowSpan || 1}
              isLastRow={isLastRow && isLastEmbeddedTableRow}
              betweenCellBorders={showCellBorders}
              isFirstColumn={cellProperties.isFirstColumn}
              isLastColumn={cellProperties.isLastColumn}
              hasRowErrorBorder={showErrorModal && hasError}
              className={cellClasses}
              style={cellStyle}
            >
              <AddEmbeddedRowCell {...cellContext} />
            </Td>
          );
        }

        return (
          <Fragment key={cell.id}>
            <Td
              isAction={cellProperties.isAction}
              cell={cell}
              isFirstColumn={cellProperties.isFirstColumn}
              isLastColumn={cellProperties.isLastColumn}
              isLastRow={isLastRow && isLastEmbeddedTableRow}
              betweenCellBorders={showCellBorders}
              hasRowErrorBorder={showErrorModal && hasError}
              className={cellClasses}
              style={cellStyle}
              rowSpan={rowSpanResult.rowSpan}
              rowWithEmbeddedTableRowInfo={rowWithEmbeddedTableRowInfo}
            />
            {showErrorModal && ErrorModalComponent && (
              <ErrorModalComponent
                error={error}
                viewMode
                ref={refs.setFloating}
                style={floatingStyles}
                numberOfErrors={numberOfErrors}
              />
            )}
          </Fragment>
        );
      })}
    </tr>
  );
}
